import SpeedIcon from "@mui/icons-material/Speed";
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Divider,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography,
} from "@mui/material";
import React from "react";
import { Link } from "react-router-dom";
import { useGetRateLimitsQuery } from "../../redux/api";
import { ROUTES } from "../../utils/routes";
import { Card, PageBody, PageHeader, StatTile } from "./components/SecurityShell";
import { formatDateTime, formatDuration } from "./securityUtils";

const Row = ({ label, value }) => (
    <TableRow>
        <TableCell sx={{ border: 0, py: 0.75, color: "text.secondary", width: "45%" }}>{label}</TableCell>
        <TableCell sx={{ border: 0, py: 0.75, fontWeight: 600 }}>{value}</TableCell>
    </TableRow>
);

const LimiterCard = ({ limiter }) => (
    <Card sx={{ flex: "1 1 340px", minWidth: 320, p: 2.5 }}>
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
            <SpeedIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>{limiter.label}</Typography>
            <Chip label={limiter.keyPrefix} size="small" variant="outlined" sx={{ fontFamily: "monospace" }} />
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {limiter.applies}
        </Typography>

        <Alert severity="info" icon={false} sx={{ mb: 1.5 }}>
            <strong>{limiter.points} requests per {limiter.duration}s</strong>
            {" — roughly "}{limiter.perSecond}/sec, {limiter.perMinute}/min, {limiter.perHour}/hour.
            {" Going over blocks that caller for "}<strong>{formatDuration(limiter.blockDuration)}</strong>.
        </Alert>

        <Table size="small">
            <TableBody>
                <Row label="Max requests (points)" value={limiter.points} />
                <Row label="Window (duration)" value={formatDuration(limiter.duration)} />
                <Row label="Block on breach" value={formatDuration(limiter.blockDuration)} />
                <Row label="Safe rate / minute" value={limiter.perMinute} />
                <Row label="Safe rate / hour" value={limiter.perHour} />
                <Row
                    label="Configured by"
                    value={
                        <Box component="span" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                            {(limiter.envVars || []).join(", ")}
                        </Box>
                    }
                />
            </TableBody>
        </Table>
    </Card>
);

/**
 * Read-only view of the rate limiter as it is actually configured. The numbers
 * come from the same constants the middleware builds its limiters from, so this
 * page cannot drift from what is enforced. Changing limits means changing the
 * env vars and restarting the service.
 */
const RateLimitDetails = () => {
    const { data, isLoading, isError } = useGetRateLimitsQuery();
    const config = data?.result;

    if (isLoading) {
        return (
            <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /></Stack>
        );
    }

    if (isError || !config) {
        return (
            <PageBody>
                <Alert severity="error" sx={{ mt: 2 }}>Could not load the rate limit configuration.</Alert>
            </PageBody>
        );
    }

    const last24h = config.last24h || {};

    return (
        <Stack direction="column" gap={2}>
            <PageHeader
                title="Rate Limits"
                subtitle="How much traffic a caller can safely send before being throttled."
            />

            <PageBody>
                {!config.enabled && (
                    <Alert severity="warning">
                        Rate limiting is currently <strong>disabled</strong> (<code>RATE_LIMIT_ENABLED</code> is not
                        "true"). The limits below are configured but not being enforced.
                    </Alert>
                )}

                <Stack direction="row" gap={2} flexWrap="wrap">
                    <StatTile
                        label="Status"
                        value={config.enabled ? "Enforcing" : "Disabled"}
                        tone={config.enabled ? "success" : "error"}
                    />
                    <StatTile
                        label="Rate limited (24h)"
                        value={Number(last24h.rateLimitedCount || 0).toLocaleString()}
                        tone={Number(last24h.rateLimitedCount) > 0 ? "warning" : "success"}
                        hint="429 responses"
                    />
                    <StatTile
                        label="Total calls (24h)"
                        value={Number(last24h.totalCalls || 0).toLocaleString()}
                        hint={last24h.since ? `since ${formatDateTime(last24h.since)}` : undefined}
                    />
                    <StatTile
                        label="Errors (24h)"
                        value={Number(last24h.errorCount || 0).toLocaleString()}
                        tone={Number(last24h.errorCount) > 0 ? "warning" : "success"}
                        hint="responses ≥ 400"
                    />
                </Stack>

                {Number(last24h.rateLimitedCount) > 0 && (
                    <Alert severity="info">
                        {Number(last24h.rateLimitedCount).toLocaleString()} requests were rejected with a 429 in the
                        last 24 hours.{" "}
                        <Link to={`${ROUTES.SECURITY_API_LOGS}?statusCode=429`}>View them in API Logs</Link>, or check
                        who is currently blocked on the{" "}
                        <Link to={ROUTES.SECURITY_BLOCKED}>Blocked IPs</Link> page.
                    </Alert>
                )}

                <Stack direction="row" gap={2} flexWrap="wrap" alignItems="stretch">
                    {(config.limiters || []).map((l) => <LimiterCard key={l.name} limiter={l} />)}
                </Stack>

                <Card sx={{ p: 2.5 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                        How callers are counted
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Blocking is not purely IP-based. What gets throttled depends on whether the request is
                        authenticated, which is why the blocked list has both an IP table and a device table.
                    </Typography>

                    <Table size="small">
                        <TableBody>
                            <Row label="Authenticated requests" value={config.keyingStrategy?.authenticated} />
                            <Row label="Anonymous requests" value={config.keyingStrategy?.anonymous} />
                            <Row label="Login / OTP paths" value={config.keyingStrategy?.authPaths} />
                            <Row
                                label="Auth path pattern"
                                value={
                                    <Box component="code" sx={{ fontSize: 12, wordBreak: "break-all" }}>
                                        {config.authPathPattern}
                                    </Box>
                                }
                            />
                        </TableBody>
                    </Table>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Behaviour notes</Typography>
                    <Stack component="ul" sx={{ pl: 2.5, m: 0, gap: 0.5 }}>
                        {(config.notes || []).map((note, i) => (
                            <Typography component="li" variant="body2" color="text.secondary" key={i}>
                                {note}
                            </Typography>
                        ))}
                    </Stack>
                </Card>
            </PageBody>
        </Stack>
    );
};

export default RateLimitDetails;
